/**
 * Reading and clearing the error that Supabase Auth appends to the OAuth
 * callback URL.
 *
 * When the provider round-trip fails, Supabase Auth redirects the browser to
 * the app with `error` / `error_code` / `error_description` — in the query
 * string, and (for older releases / `redirectErrors`) mirrored in the hash
 * fragment. Two things have to happen with it:
 *
 *   1. Read it *before* routing. The callback lands on a path the router
 *      rewrites (see `captureAuthError`), and a router redirect replaces the
 *      whole URL, silently dropping the params.
 *   2. Strip it afterwards, so a reload does not resurrect the message.
 *
 * Pure (takes a URL string, touches no DOM) so it stays unit-testable in
 * `tests/unit/oauthError.test.ts`.
 */

/**
 * Exact copy for a sign-up rejected by the closed-beta cap (`app_config.max_users`,
 * `20260225000000_remove_invites_restore_user_cap.sql`). Kept here (not read off
 * the URL) so the message is stable regardless of how the callback encodes it.
 * It must keep the "closed beta" phrase `readAuthError` matches on, which is
 * the wording the `before_user_created` hook returns.
 */
export const BETA_FULL_MESSAGE =
  "FaB Judge is in closed beta and is currently full. Please check back again soon.";

/** The parameters Supabase Auth uses to carry an error. */
const ERROR_KEYS = ["error", "error_code", "error_description"] as const;

/** A sign-in failure carried back on the callback URL. */
export interface AuthError {
  /** Message to show on the login screen. */
  message: string;
  /** True when the closed-beta cap refused the sign-up. */
  betaFull: boolean;
}

/** Prefer the query string, then fall back to the hash fragment. */
function pick(
  search: URLSearchParams,
  hash: URLSearchParams,
  key: string,
): string {
  return search.get(key) || hash.get(key) || "";
}

function split(
  url: string,
): { search: URLSearchParams; hash: URLSearchParams } {
  const parsed = new URL(url);
  return {
    search: parsed.searchParams,
    hash: new URLSearchParams(parsed.hash.replace(/^#/, "")),
  };
}

/**
 * The callback's error, or null when the URL carries none (a successful
 * callback, or a clean URL).
 *
 * Only a genuine closed-beta rejection gets the beta copy, keyed off the wording
 * the cap hook returns; anything else speaks for itself, so an already-registered
 * user whose Google account is in a bad state is never told the beta is full.
 */
export function readAuthError(url: string): AuthError | null {
  const { search, hash } = split(url);
  const code = pick(search, hash, "error_code");
  const error = pick(search, hash, "error");
  const rawDescription = pick(search, hash, "error_description");
  if (!code && !error && !rawDescription) return null;

  // `URLSearchParams` already decodes `+`; the replace keeps a legacy
  // (double-encoded) description readable.
  const description = rawDescription.replace(/\+/g, " ");

  // Both layers of the gate use the same phrase: the hook's 403 message and the
  // `enforce_user_cap` trigger's exception.
  if (/closed beta/i.test(description) || /closed beta/i.test(code)) {
    return { message: BETA_FULL_MESSAGE, betaFull: true };
  }
  if (error === "access_denied") {
    return { message: "Sign-in was cancelled.", betaFull: false };
  }
  return {
    message: description || "Could not sign in. Please try again.",
    betaFull: false,
  };
}

/**
 * The same URL with the error params removed from the query string and the
 * hash. Unrelated params (e.g. the PKCE `code` on a successful callback) are
 * preserved, as is the path.
 */
export function stripAuthError(url: string): string {
  const parsed = new URL(url);
  for (const key of ERROR_KEYS) parsed.searchParams.delete(key);

  // Any error in the fragment takes the whole fragment with it: it only ever
  // holds the callback's params, and Supabase Auth adds a bare `sb` marker.
  const hash = new URLSearchParams(parsed.hash.replace(/^#/, ""));
  if (ERROR_KEYS.some((key) => hash.has(key))) parsed.hash = "";

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
